import { useEffect, useRef } from "react";

import { GoogleApiRequestTypes } from "@shared/constants/google-api-request-types.enum";
import api from "@services/api";
import { useAuth } from "@hooks/useAuth";

/** User fields sent with each Google API usage log */
interface GoogleApiUsageLogUser {
  name?: string;
  email?: string;
}

/**
 * Send one Google API usage record to the Elastic index (through POST /logs/save).
 * Fire and forget: a failed log never interrupts the map.
 * @param description What was requested from Google
 * @param user Logged user
 */
export const saveGoogleApiUsageLog = (description: GoogleApiRequestTypes, user?: GoogleApiUsageLogUser): void => {
  try {
    Promise.resolve(api.post("/logs/save", {
      context: "Frontend",
      description,
      user: user ? { name: user.name, email: user.email } : null
    })).catch((error) => console.log(error));
  } catch (error) {
    console.log(error);
  }
};

/**
 * Log each Google map created (one Dynamic Maps load) and each Street View panorama opened on it.
 * @param map Map instance returned by useGoogleMaps (undefined until the API loads)
 * @param description Which map was loaded
 */
export const useGoogleMapUsageLog = (map: google.maps.Map | undefined, description: GoogleApiRequestTypes): void => {

  const { user } = useAuth();
  const userRef = useRef(user);

  userRef.current = user;

  useEffect(() => {

    if (!map) return undefined;

    saveGoogleApiUsageLog(description, userRef.current);

    // The app has no error boundary: an error thrown here would blank the whole page, so the log never throws
    let streetViewListener: google.maps.MapsEventListener | undefined;

    try {
      const streetView = map.getStreetView();

      streetViewListener = streetView.addListener("visible_changed", () => {
        try {
          if (streetView.getVisible()) saveGoogleApiUsageLog(GoogleApiRequestTypes.STREET_VIEW_PANORAMA, userRef.current);
        } catch (error) {
          console.log(error);
        }
      });
    } catch (error) {
      console.log(error);
    }

    return () => streetViewListener?.remove();

  }, [map, description]);
};
